import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Modal, Typography, Divider, Loading, Button, HoverableIconBtn } from 'ck-ui';
import { getImgixUrl } from 'ck-utils';
import { getSalePercentage, getPriceString } from 'ck-utils/price';
import { trackCustomEventThenRedirect } from 'ck-utils/algoliaAnalytics';
import { useMedia } from 'react-use';
import { Configure, InstantSearch } from 'react-instantsearch/dom';
import { connectHits, connectStateResults } from 'react-instantsearch-dom';
import { algoliaClient, INDEX_COLLECTIONS, userToken } from 'src/lib/algolia';
import { ProductListingPageContext } from '../ProductListingPage/context';
import styles from './BundleIconSash.module.scss';

const BundleIconCollectionHit = ({ isSearchStalled, hits, isMobile, productHandle }) => {
  const { bundleImage, lookImage } = useContext(ProductListingPageContext);
  const [showButtons, setShowButtons] = useState(true);

  // Render Loading
  if (isSearchStalled) {
    return <Loading isLoading />;
  }

  // Early exit if no hits returned
  if (hits.length === 0) return null;

  // push new bundles and latest looks
  if (!isMobile && hits.length <= 2) {
    hits.push({ image: bundleImage, handle: 'bundles', type: 'bundle' });
    if (hits.length === 2) {
      hits.push({ image: lookImage, handle: 'shop-the-look', type: 'look' });
    }
    setShowButtons(false);
  }

  // Handle onClick tracking and redirect
  const pageHandle = window.location.pathname.split('/').pop();
  const onClick = (url) => (e) => {
    e.preventDefault();
    // event tagging
    trackCustomEventThenRedirect({
      event: 'bundlePopupClick',
      url,
      data: {
        bundlePopupLabel: `${pageHandle} > ${productHandle} > ${url.split('/').pop()}`,
      },
    });
  };

  // More Constants
  const bundlesUrl = '/collections/bundles';
  const looksUrl = '/collections/shop-the-look';

  return (
    <>
      <div className={classnames('is-flex', styles.columns, { [styles.centered]: hits.length === 1 })}>
        {hits.map((hit) => {
          const { compareAtPrice, image, price, title, handle, isBundle, type } = hit;
          const hitUrl = `/collections/${handle}`;
          return (
            <div key={handle} className={styles.column}>
              <a
                className={classnames('product-tile-image-wrapper', styles.imgRoot)}
                href={hitUrl}
                onClick={onClick(hitUrl)}
              >
                <img alt={title} src={getImgixUrl(image)} />
                {isBundle && (
                  <div className="sash-base collection-text-sash">
                    <span className="sash-text has-background-danger has-text-white">
                      {`Save ${getSalePercentage(price, compareAtPrice)}%`}
                    </span>
                  </div>
                )}
                {!type && !isMobile && (
                  <div className={classnames('is-flex-centered', 'is-full-width', 'is-full-height', 'is-hidden', styles.shop)}>
                    <Button secondary color="white">{isBundle ? 'SHOP BUNDLE' : 'SHOP LOOK'}</Button>
                  </div>
                )}
              </a>
              {!type && (
                <div className={styles.description}>
                  <Typography
                    variant={isMobile ? 'body' : 'heading8'}
                    color="black-bis"
                    uppercase
                    className={styles.marginTop}
                    weight={700}
                  >
                    {title}
                  </Typography>
                  <div className={styles.marginTop}>
                    {isBundle && <Typography color="danger">{getPriceString(price)}</Typography>}
                    {isBundle && <Typography color="grey-dark" strikeThrough className={styles.marginLeft}>{getPriceString(compareAtPrice)}</Typography>}
                  </div>
                </div>
              )}
              <div className={classnames('is-flex', styles.btnRoot)}>
                {type === 'bundle' && <Button size="medium" renderAs="a" href={bundlesUrl} onClick={onClick(bundlesUrl)}>SHOP ALL BUNDLES</Button>}
                {type === 'look' && <Button size="medium" renderAs="a" className={styles.minWidth} href={looksUrl} onClick={onClick(looksUrl)}>SHOP ALL LOOKS</Button>}
              </div>
            </div>
          );
        })}
      </div>
      {isMobile && <Divider />}
      {(showButtons || isMobile) && (
        <div className={classnames('is-full-width has-text-centered', styles.bottomBtnRoot)}>
          <Button
            size="medium"
            className="fullwidth-mobile"
            renderAs="a"
            href={bundlesUrl}
            onClick={onClick(bundlesUrl)}
          >
            SHOP ALL BUNDLES
          </Button>
          <Button
            size="medium"
            className={classnames(styles.minWidth, styles.lookBtn, 'fullwidth-mobile')}
            renderAs="a"
            href={looksUrl}
            onClick={onClick(looksUrl)}
          >
            SHOP ALL LOOKS
          </Button>
        </div>
      )}
    </>
  );
};
BundleIconCollectionHit.propTypes = {
  hits: PropTypes.array.isRequired,
  isSearchStalled: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  productHandle: PropTypes.string.isRequired,
};

const BundleIconSash = ({
  handles, productHandle
}) => {
  const [show, setShow] = useState(false);
  const isMobile = useMedia('(max-width: 767px)');
  const pageHandle = window.location.pathname.split('/').pop();
  const filters = `isOnline:true AND blockOnSiteSearch:false AND (handle:${handles.join(' OR handle:')})`;

  const onClose = () => {
    setShow(false);
  };
  const onOpen = () => {
    setShow(true);
    window.dataLayer.push({
      event: 'bundlePopupView',
      bundlePopupLabel: `${pageHandle} > ${productHandle}`,
    });
  };
  return (
    <>
      <HoverableIconBtn title="See bundles" onClick={onOpen} iconName="bundle" text="See Bundles" />
      <Modal show={show} onClose={onClose} bottomCloseText="Close Window" isFixedSize>
        <div className={classnames('is-flex', styles.modalRoot)}>
          <Typography variant={isMobile ? 'heading7' : 'heading5'}>THIS PRODUCT FEATURES IN…</Typography>
          <Divider className={styles.dividerMarginTop} />
          <InstantSearch indexName={INDEX_COLLECTIONS} searchClient={algoliaClient}>
            <Configure userToken={userToken} hitsPerPage={100} filters={filters} analyticsTags={['web', 'bundle-sash']} />
            <BundleIconCollectionContainer isMobile={isMobile} productHandle={productHandle} />
          </InstantSearch>
        </div>
      </Modal>
    </>
  );
};

BundleIconSash.propTypes = {
  handles: PropTypes.array.isRequired,
  productHandle: PropTypes.string.isRequired,
};

const BundleIconCollectionContainer = connectHits(connectStateResults(BundleIconCollectionHit));
export { BundleIconSash };
